import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  forwardRef,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import {
  CompletionContext,
  CompletionResult,
  autocompletion,
} from '@codemirror/autocomplete';
import {
  cursorDocEnd,
  defaultKeymap,
  history,
  historyKeymap,
} from '@codemirror/commands';
import {
  javascript,
  javascriptLanguage,
  scopeCompletionSource,
} from '@codemirror/lang-javascript';
import {
  defaultHighlightStyle,
  syntaxHighlighting,
} from '@codemirror/language';
import { Annotation, EditorState } from '@codemirror/state';
import { EditorView, keymap, tooltips } from '@codemirror/view';

@Component({
  selector: 'lib-code-mirror-editor',
  templateUrl: './code-mirror-editor.component.html',
  styleUrls: ['./code-mirror-editor.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CodeMirrorEditorComponent),
      multi: true,
    },
  ],
})
export class CodeMirrorEditorComponent
  implements OnInit, AfterViewInit, OnDestroy, ControlValueAccessor
{
  @Input()
  get data() {
    return this._data;
  }
  set data(val) {
    this._data = val;
  }
  private _data: string[] = [];

  @ViewChild('container') container: ElementRef<HTMLDivElement> | undefined;
  editorView: EditorView | undefined;
  state!: EditorState;
  private _cache: string | undefined;

  /**
   * Annotation used to mark transactions that were caused by the control value accessor
   */
  private fcWriteValueAnnotation = Annotation.define<string>();

  ngOnInit() {
    this.state = EditorState.create({
      extensions: [
        keymap.of([...defaultKeymap, ...historyKeymap]),
        javascriptLanguage.data.of({
          autocomplete: this.myCompletions(),
        }),
        history(),
        javascript(),
        autocompletion({}),
        syntaxHighlighting(defaultHighlightStyle),
        EditorView.lineWrapping,
        EditorView.updateListener.of((update) => {
          if (update.docChanged) {
            // if all transactions have the fcWriteValueAnnotation,
            // then we don't need to call the onChangeFn because the
            // change was made by the control value accessor
            if (
              update.transactions.every((t) =>
                t.annotation(this.fcWriteValueAnnotation),
              )
            ) {
              return;
            }

            this._onChangeFn?.(
              this._cache ?? update.state.doc.toString().trim(),
            );
          } else if (this._cache) {
            this.writeValue?.(this._cache);
            this._cache = undefined;
          }
        }),
        tooltips({
          parent: window.document.body,
        }),
      ],
    });
  }

  ngAfterViewInit(): void {
    this.editorView = new EditorView({
      parent: this.container?.nativeElement,
      state: this.state,
    });
  }
  ngOnDestroy(): void {
    this.editorView?.destroy();
  }

  myCompletions() {
    return (context: CompletionContext) => {
      const completions = scopeCompletionSource(this.data)(context);
      //console.log(completions);
      if (completions) {
        (completions as CompletionResult).options = (
          completions as CompletionResult
        ).options.filter((val) => val.boost == 0);
      }
      return completions;
    };
  }
  private _onChangeFn: ((_: string) => void) | null = null;

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onTouched = () => {};

  writeValue(obj: string | null | undefined): void {
    if (this.editorView && obj) {
      //console.log('giusto', obj);
      //console.log(this.editorView.state.doc.length);
      this.editorView?.dispatch({
        changes: [
          {
            from: 0,
            to: this.editorView.state.doc.length,
            insert: obj ?? '',
          },
        ],
        annotations: [this.fcWriteValueAnnotation.of(obj)],
      });
    } else {
      if (obj) this._cache = obj;
    }
  }

  registerOnChange(fn: (_: string) => void): void {
    //console.log('registerOnChange');
    this._onChangeFn = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  updateCursor() {
    const editor = this.editorView;
    if (!editor) return;

    editor.focus();
    cursorDocEnd({
      state: editor.state,
      dispatch: editor.dispatch,
    });
  }
}
